import {DataStatus, TabProps, Tabs} from 'platform/components';
import {VStack} from 'platform/foundation';

import {groupBy, isEmpty, isNil} from 'ramda';

import {useGetEmployeeAuthorizationIntegrationQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {buildArray, isNotNilOrEmpty, useQueryState} from 'shared/web';

import {IntegrationBox} from './components/IntegrationBox';
import {INTEGRATION_CODES} from './constants/IntegrationCodes';
import {OPERATORS} from './constants/operators';

interface UserExternalSystemsProps {
  userId: string | undefined;
}

export function UserExternalSystems(props: UserExternalSystemsProps) {
  const [activeTabId, setActiveTabId] = useQueryState('selected-operator');

  const {data: integrationData, isLoading: isLoadingIntegrations} =
    useGetEmployeeAuthorizationIntegrationQuery(
      {
        userId: props.userId ?? '',
      },
      {skip: isNil(props.userId)}
    );

  const integrationsByCode = groupBy(
    (integration) => integration.integrationCode,
    integrationData ?? []
  );

  const mercedesProfiles = [
    {
      integrationCode: INTEGRATION_CODES.MERCEDES_XENTRY,
      integrations: integrationsByCode[INTEGRATION_CODES.MERCEDES_XENTRY],
    },
    {
      integrationCode: INTEGRATION_CODES.MERCEDES_CLARA,
      integrations: integrationsByCode[INTEGRATION_CODES.MERCEDES_CLARA],
    },
    {
      integrationCode: INTEGRATION_CODES.MERCEDES_DAIMLER_XENTRY,
      integrations: integrationsByCode[INTEGRATION_CODES.MERCEDES_DAIMLER_XENTRY],
    },
  ].filter((item) => isNotNilOrEmpty(item.integrations));

  const otherProfiles = [
    {
      integrationCode: INTEGRATION_CODES.OTHER_SAG,
      integrations: integrationsByCode[INTEGRATION_CODES.OTHER_SAG],
    },
    {
      integrationCode: INTEGRATION_CODES.OTHER_TECRMI,
      integrations: integrationsByCode[INTEGRATION_CODES.OTHER_TECRMI],
    },
  ].filter((item) => isNotNilOrEmpty(item.integrations));

  const isWithoutExternalSystems = [mercedesProfiles, otherProfiles].every(isEmpty);

  const tabs = buildArray<TabProps>()
    .when(mercedesProfiles.length > 0, {
      id: OPERATORS.MERCEDES,
      title: i18n.t('page.employeeDetail.externalSystems.mercedes'),
      content: (
        <VStack spacing={4}>
          {mercedesProfiles.map((profiles) => (
            <IntegrationBox
              key={profiles.integrationCode}
              integrationCode={profiles.integrationCode}
              integrations={profiles.integrations ?? []}
              isLoading={isLoadingIntegrations}
              userId={props.userId}
            />
          ))}
        </VStack>
      ),
    })
    .when(otherProfiles.length > 0, {
      id: OPERATORS.OTHER,
      title: i18n.t('page.employeeDetail.externalSystems.other'),
      content: (
        <VStack spacing={4}>
          {otherProfiles.map((profiles) => (
            <IntegrationBox
              key={profiles.integrationCode}
              integrationCode={profiles.integrationCode}
              integrations={profiles.integrations ?? []}
              isLoading={isLoadingIntegrations}
              userId={props.userId}
            />
          ))}
        </VStack>
      ),
    });

  return (
    <VStack spacing={4}>
      <DataStatus
        isEmpty={isWithoutExternalSystems}
        isLoading={isLoadingIntegrations}
        spacing={20}
        emptySubheadline={i18n.t('page.employeeDetail.labels.noExternalSystems')}
      >
        <Tabs tabs={tabs} activeTabId={activeTabId} onChange={setActiveTabId} />
      </DataStatus>
    </VStack>
  );
}
